
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { useAuth } from './useAuth';

interface Phase {
  id: string;
  group_id: string;
  name: string;
  phase_number: number;
  created_at: string;
  updated_at: string;
}

export const usePhaseManagement = () => {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchPhases = useCallback(async (groupId: string) => {
    if (!groupId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('phases')
        .select('*')
        .eq('group_id', groupId)
        .order('phase_number');

      if (error) throw error;
      setPhases(data || []);
    } catch (error) {
      console.error('Error fetching phases:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել փուլերը",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const addPhase = useCallback(async (groupId: string, phaseName: string) => {
    if (!user) {
      toast({
        title: "Սխալ",
        description: "Օգտատեր չի հայտնաբերվել",
        variant: "destructive"
      });
      return false;
    }

    setLoading(true);
    try {
      // Get the next phase number
      const { data: existingPhases, error: fetchError } = await supabase
        .from('phases')
        .select('phase_number')
        .eq('group_id', groupId)
        .order('phase_number', { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;

      const nextPhaseNumber = existingPhases && existingPhases.length > 0 
        ? existingPhases[0].phase_number + 1 
        : 1;

      const { error } = await supabase
        .from('phases')
        .insert({
          group_id: groupId,
          name: phaseName,
          phase_number: nextPhaseNumber
        });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Փուլը ավելացվեց"
      });

      await fetchPhases(groupId);
      return true;
    } catch (error) {
      console.error('Error adding phase:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ավելացնել փուլը",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast, fetchPhases]);

  const updatePhase = useCallback(async (phaseId: string, updates: Partial<Phase>) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('phases')
        .update(updates)
        .eq('id', phaseId);

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Փուլը թարմացվեց"
      });

      return true;
    } catch (error) {
      console.error('Error updating phase:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց թարմացնել փուլը",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const deletePhase = useCallback(async (phaseId: string, groupId: string) => {
    setLoading(true);
    try {
      // Check if phase has lessons
      const { data: lessons, error: lessonsError } = await supabase
        .from('lessons')
        .select('id')
        .eq('phase_id', phaseId)
        .limit(1);

      if (lessonsError) throw lessonsError;

      if (lessons && lessons.length > 0) {
        toast({
          title: "Սխալ",
          description: "Չի կարող ջնջել փուլը, որը պարունակում է դասեր",
          variant: "destructive"
        });
        return false;
      }

      const { error } = await supabase
        .from('phases')
        .delete()
        .eq('id', phaseId);

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Փուլը ջնջվեց"
      });

      await fetchPhases(groupId);
      return true;
    } catch (error) {
      console.error('Error deleting phase:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ջնջել փուլը",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchPhases]);

  return {
    phases,
    loading,
    fetchPhases,
    addPhase,
    updatePhase,
    deletePhase
  };
};
